import { HoverPopover } from '@app/components/base/HoverPopover';
import { LDate } from '@app/components/date/LDate';
import { LTime } from '@app/components/date/LTime';
import { AuditSchedule } from '@mi-front/core';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { Chip } from '@mui/material';
import { SchedulesListCategory } from './build-schedules-list-order-filter-url';
import { ConfirmedByScheduleDetails } from './ConfirmedByScheduleDetails';

type Props = {
  schedule: AuditSchedule;
  showDate?: boolean;
  confirmationType?: 'DEFAULT' | 'OFF_TOLERANCE' | null;
  /** Match datetime used to compute diff when `diffMinutes` is omitted. */
  matchDatetime?: string | null;
  /** Match vs schedule difference in minutes (preferred when provided by the API). */
  diffMinutes?: number | null;
  /**
   * Which schedules list to open when filtering by order.
   * Defaults from `schedule.type` (`MENTION` → mentions, otherwise spot).
   */
  schedulesListCategory?: SchedulesListCategory;
};

/**
 * Resolves spot vs mentions schedules list from an explicit prop or schedule type.
 */
function resolveSchedulesListCategory(
  schedule: AuditSchedule,
  schedulesListCategory?: SchedulesListCategory,
): SchedulesListCategory {
  if (schedulesListCategory != null) {
    return schedulesListCategory;
  }

  return schedule.type === 'MENTION' ? 'mentions' : 'spot';
}

/**
 * Whether a match falls outside the schedule's before/after tolerance window.
 * Falls back to legacy tolerance fields when `toleranceConfig` is absent.
 */
function isDiffOffTolerance(schedule: AuditSchedule, diffMinutes: number): boolean {
  const before = schedule.toleranceConfig?.before?.value ?? schedule.toleranceBefore;
  const after = schedule.toleranceConfig?.after?.value ?? schedule.tolerance;

  if (diffMinutes < 0) {
    return before != null && Math.abs(diffMinutes) > before;
  }

  return after != null && diffMinutes > after;
}

/**
 * Resolves match-vs-schedule diff in minutes from explicit value or datetimes.
 */
function resolveDiffMinutes(
  schedule: AuditSchedule,
  matchDatetime: string | null | undefined,
  diffMinutes: number | null | undefined,
): number | null {
  if (diffMinutes != null) {
    return diffMinutes;
  }

  if (matchDatetime == null) {
    return null;
  }

  return (new Date(matchDatetime).valueOf() - new Date(schedule.datetime).valueOf()) / 60000;
}

/**
 * Schedule confirmation chip (schedule time + icon). Tolerance and diff live in a hover popover.
 */
export function ConfirmedBySchedule({
  schedule,
  showDate = false,
  confirmationType,
  matchDatetime,
  diffMinutes: diffMinutesProp,
  schedulesListCategory,
}: Props) {
  const diffMinutes = resolveDiffMinutes(schedule, matchDatetime, diffMinutesProp);

  const isOffTolerance =
    confirmationType === 'OFF_TOLERANCE' ||
    (confirmationType == null && diffMinutes != null && isDiffOffTolerance(schedule, diffMinutes));

  const listCategory = resolveSchedulesListCategory(schedule, schedulesListCategory);

  return (
    <HoverPopover
      trigger={
        <Chip
          icon={<EventAvailableIcon />}
          label={
            <>
              {showDate && (
                <>
                  <LDate value={schedule.datetime} day="2-digit" month="2-digit" />{' '}
                </>
              )}
              <LTime value={schedule.datetime} />
            </>
          }
          size="small"
          variant="outlined"
          color={isOffTolerance ? 'warning' : 'success'}
        />
      }
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      paperSx={{ mt: 1 }} // gap below the trigger (theme spacing × 1 = 8px)
    >
      <ConfirmedByScheduleDetails
        schedule={schedule}
        diffMinutes={diffMinutes}
        isOffTolerance={isOffTolerance}
        schedulesListCategory={listCategory}
      />
    </HoverPopover>
  );
}
